import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Modal,
  Backdrop,
  Fade,
  Button,
  Paper,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  TextField,
} from "@mui/material";
import {
  YouTube as YouTubeIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Poll as PollIcon,
  AttachMoney as AttachMoneyIcon,
  HelpOutline as HelpOutlineIcon,
  Casino as CasinoIcon,
} from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import QuickReactions from "../components/LiveStream/UserInteraction/QuickReactions";
import PollSection from "../components/LiveStream/UserInteraction/PollSection";
import TipSection from "../components/LiveStream/UserInteraction/TipSection";
import QuestionSection from "../components/LiveStream/UserInteraction/QuestionSection";
import { Navigate, useSearchParams, useParams } from "react-router-dom";
import { useGetBookingById } from "../services/stagemode_api/talent/talent_api";
import dayjs from "dayjs";
import { LocationOn, AccessTime, Event, Poll } from "@mui/icons-material";
import YouTubeRequestSection from "./YoutubeRequestSection";
import StageInviteListener from "./StageInviteListener";
import BingoWrapper from "./Bingo/UserBingoWrapper";
import axios from "axios";
import { useJoinGuest } from "../services/stagemode_api/viewers/viewers_api";
import PerformerInvitation from "../components/LiveStream/UserInteraction/PerformerInvitation";
import ModeratorInvitation from "../components/LiveStream/UserInteraction/ModeratorInviation";

//------- App-like mobile UX: anchors + sticky header + bottom dock

const API = axios.create({
  baseURL: (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, ""),
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
  border: "rgba(255,255,255,0.12)",
};

const UserInteractionPage: React.FC = () => {
  const [params] = useSearchParams();
  const { channel_name } = useParams<{ channel_name: string }>();
  const bookingId = params.get("bookingId");
  const channel = channel_name || "";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const youtubeRef = useRef<HTMLDivElement>(null);
  const inviteRef = useRef<HTMLDivElement>(null);
  const moderateRef=useRef<HTMLDivElement>(null);
  const reactRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const qaRef = useRef<HTMLDivElement>(null);
  const bingoRef = useRef<HTMLDivElement>(null);
  const { update } = useJoinGuest({
    params: { id: bookingId ?? "" },
    key: ["joinGuest"],
  });
  const { data: booking } = useGetBookingById(bookingId ?? "");

  // Intro + name
  const [showIntro, setShowIntro] = useState(true);
  const [displayName, setDisplayName] = useState<string>("");

  // Per-booking keys
  const keys = useMemo(() => {
    const id = bookingId || "unknown";
    return {
      nameKey: `stagelink:booking:${id}:name`,
      joinedKey: `stagelink:booking:${id}:joinedName`,
      participantKey: `stagelink:booking:${id}:participantId`,
    };
  }, [bookingId]);

  const participantId = localStorage.getItem(keys.participantKey);

  useEffect(() => {
    if (!channel) return;
    const saved = localStorage.getItem(keys.nameKey);
    if (saved) setDisplayName(saved);
  }, [channel_name, keys.nameKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (displayName.trim()) setShowIntro(false);
    }, 3000);
    return () => clearTimeout(t);
  }, [displayName]);

  const deviceHint = useMemo(() => {
    try {
      const ua = navigator.userAgent || "";
      let h = 0;
      for (let i = 0; i < ua.length; i++) h = (h * 31 + ua.charCodeAt(i)) | 0;
      return String(h);
    } catch {
      return "0";
    }
  }, []);

  const registerGuestIfNeeded = async () => {
    if (!bookingId) return;
    const trimmed = displayName.trim();
    if (!trimmed) return;

    const alreadyJoinedWith = localStorage.getItem(keys.joinedKey);
    if (alreadyJoinedWith && alreadyJoinedWith === trimmed) return;

    try {
      const res = await update({ guest_name: trimmed });
      if (res.data?.participantId) {
        localStorage.setItem(keys.participantKey, String(res.data.participantId));
      }
      localStorage.setItem(keys.nameKey, trimmed);
      localStorage.setItem(keys.joinedKey, trimmed);
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        localStorage.setItem(keys.nameKey, trimmed);
        localStorage.setItem(keys.joinedKey, trimmed);
        return;
      }
      console.error("Guest join failed:", err?.response?.data || err?.message || err);
    }
  };

  useEffect(() => {
    if (!showIntro && channel_name && displayName.trim()) {
      registerGuestIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIntro, channel_name, displayName]);

  if (!bookingId || !channel_name) {
    return <Navigate to="/" />;
  }

  const bookingData = booking?.data;
 
  

  const scrollTo = (ref: React.RefObject<HTMLElement>) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* Intro / Name capture */}
      <Modal
        open={showIntro}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: "rgba(0, 0, 0, 0.85)" },
          },
        }}
      >
        <Fade in={showIntro}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: COLORS.panel,
              color: COLORS.text,
              px: 4,
              py: 4,
              borderRadius: 3,
              boxShadow: "0 24px 80px rgba(0,0,0,0.65)",
              textAlign: "center",
              minWidth: 320,
              width: { xs: "92vw", sm: 420 },
              border: `1px solid ${COLORS.border}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Lexend",
                fontWeight: 800,
                mb: 1.5,
                color: COLORS.red,
                letterSpacing: "-0.01em",
              }}
            >
              🎬 Welcome to StageLink<sup style={{ fontSize: "0.6em" }}>TM</sup>
            </Typography>

            <Typography
              variant="body2"
              sx={{ fontFamily: "Lexend", color: COLORS.textMuted, mb: 2 }}
            >
              Enter a display name so Talent and Moderator can see you in the audience list.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              inputProps={{ maxLength: 32 }}
              sx={{
                mb: 2,
                "& .MuiInputBase-input": {
                  color: COLORS.text,
                  fontFamily: "Lexend",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.border,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.textMuted,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: COLORS.red,
                },
              }}
            />

            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                onClick={() => setShowIntro(false)}
                disabled={!displayName.trim()}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend",
                  fontWeight: 700,
                  bgcolor: COLORS.red,
                  "&:hover": { bgcolor: COLORS.redHover },
                }}
              >
                Continue
              </Button>
              <Button
                onClick={() => setShowIntro(false)}
                variant="outlined"
                sx={{
                  color: COLORS.text,
                  borderColor: COLORS.red,
                  textTransform: "none",
                  fontFamily: "Lexend",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: COLORS.red,
                    borderColor: COLORS.red,
                  },
                }}
              >
                Skip Intro
              </Button>
            </Stack>

            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1.25, color: COLORS.textMuted }}
            >
              Channel: <strong style={{ color: COLORS.text }}>{channel}</strong>
            </Typography>
          </Box>
        </Fade>
      </Modal>

      {!showIntro && (
        <StageInviteListener channel={channel} participantId={participantId} />
      )}

      {/* Main App Surface */}
      {!showIntro && (
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: COLORS.bg,
            color: COLORS.text,
            pb: { xs: "calc(72px + env(safe-area-inset-bottom))", sm: 6 }, // room for bottom dock
            // iOS-like momentum + keep keyboard from pushing layout too much
            overscrollBehaviorY: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {/* Sticky translucent header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backdropFilter: "blur(10px)",
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.35))",
              borderBottom: `1px solid ${COLORS.border}`,
              px: 2,
              py: 1.25,
            }}
          >
            <Container maxWidth="md" disableGutters sx={{ px: { xs: 0.5, sm: 0 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Lexend",
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2,
                  textAlign: "center",
                }}
              >
                StageLink<sup style={{ fontSize: "0.6em" }}>TM</sup>
              </Typography>
            </Container>
          </Box>

          <Container maxWidth="md" sx={{ px: { xs: 1.25, sm: 2 }, pt: 2 }}>
            {/* Event Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.75, sm: 3 },
                mb: { xs: 2, sm: 3 },
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, #111 0%, #1b1b1b 100%)",
                color: "white",
                position: "relative",
                overflow: "hidden",
                border: `1px solid ${COLORS.border}`,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(45deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)",
                  pointerEvents: "none",
                },
              }}
            >
              <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "1.25rem", sm: "1.75rem" },
                      letterSpacing: "-0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    {bookingData?.stagecard?.title}
                  </Typography>

                  {/* Short description on larger screens */}
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 2,
                        opacity: 0.9,
                        fontSize: "1rem",
                        lineHeight: 1.6,
                        color: COLORS.textMuted,
                      }}
                    >
                      {bookingData?.stagecard?.short_description}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flexWrap: "wrap",
                      mt: 0.5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.95rem", sm: "1.05rem" },
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <LocationOn sx={{ fontSize: "1.2rem" }} />
                        {bookingData?.venue_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          ml: 4,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {bookingData?.venue_location}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                      <Chip
                        icon={<Event sx={{ fontSize: "1.05rem", color: "white" }} />}
                        label={dayjs(bookingData?.start_date_time).format("MMM D, YYYY")}
                        sx={{
                          color: "white",
                          backgroundColor: "rgba(255,255,255,0.1)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backdropFilter: "blur(10px)",
                          fontSize: { xs: "0.72rem", sm: "0.85rem" },
                        }}
                      />
                      <Chip
                        icon={<AccessTime sx={{ fontSize: "1.05rem", color: "white" }} />}
                        label={`${dayjs(bookingData?.start_date_time).format(
                          "h:mm A"
                        )} - ${dayjs(bookingData?.end_date_time).format("h:mm A")}`}
                        sx={{
                          color: "white",
                          backgroundColor: "rgba(255,255,255,0.1)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backdropFilter: "blur(10px)",
                          fontSize: { xs: "0.72rem", sm: "0.85rem" },
                        }}
                      />
                    </Stack>
                  </Box>
                </Grid>

                {/* Talent */}
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Avatar
                      src={bookingData?.stagecard?.talent?.profile_image}
                      sx={{
                        width: { xs: 64, sm: 72 },
                        height: { xs: 64, sm: 72 },
                        mb: 1,
                        border: `2px solid ${COLORS.border}`,
                        boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        mb: 0.25,
                        color: "#fff",
                      }}
                    >
                      {bookingData?.stagecard?.talent?.username}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.75,
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                        maxWidth: 180,
                        mx: "auto",
                        color: "#d1d5db",
                      }}
                    >
                      {bookingData?.stagecard?.talent?.bio}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Sections (with anchors) */}
            <Box ref={youtubeRef} sx={{ mb: 2 }}>
              <YouTubeRequestSection
                channel={channel}
                bookingId={bookingId}
                participantId={participantId}
                displayName={displayName}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} ref={inviteRef}>
                <PerformerInvitation bookingId={bookingId} />
              </Grid>
              <Grid item xs={12} ref={moderateRef}>
                <ModeratorInvitation bookingId={bookingId} />
              </Grid>
              <Grid item xs={12} ref={reactRef}>
                <QuickReactions bookingId={bookingId} />
              </Grid>
              <Grid item xs={12} ref={pollRef}>
                <PollSection bookingId={bookingId} />
              </Grid>
              <Grid item xs={12} ref={tipRef}>
                <TipSection bookingId={bookingId} />
              </Grid>
              <Grid item xs={12} ref={qaRef}>
                <QuestionSection bookingId={bookingId} />
              </Grid>
            </Grid>

            <Box ref={bingoRef} sx={{ pt: 2 }}>
              <BingoWrapper username={displayName} />
            </Box>
          </Container>

          {/* Bottom Dock (mobile) */}
         <Paper
  elevation={0}
  square
  sx={{
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    bgcolor: "rgba(15,15,15,0.92)",
    borderTop: `1px solid ${COLORS.border}`,
    backdropFilter: "blur(10px)",
    px: 1.5,
    pt: 0.75,
    pb: "calc(8px + env(safe-area-inset-bottom))",
    display: { xs: "block", sm: "none" },
  }}
>
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{ maxWidth: 480, mx: "auto" }}
  >
    <Tooltip title="Requests">
      <IconButton
        aria-label="Requests"
        onClick={() => scrollTo(youtubeRef)}
        sx={{ color: "#fff" }}
      >
        <YouTubeIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Reactions">
      <IconButton
        aria-label="Reactions"
        onClick={() => scrollTo(reactRef)}
        sx={{ color: "#fff" }}
      >
        <EmojiEmotionsIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Polls">
      <IconButton
        aria-label="Polls"
        onClick={() => scrollTo(pollRef)}
        sx={{ color: "#fff" }}
      >
        <PollIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Tips">
      <IconButton
        aria-label="Tips"
        onClick={() => scrollTo(tipRef)}
        sx={{ color: "#fff" }}
      >
        <AttachMoneyIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Q&A">
      <IconButton
        aria-label="Q and A"
        onClick={() => scrollTo(qaRef)}
        sx={{ color: "#fff" }}
      >
        <HelpOutlineIcon />
      </IconButton>
    </Tooltip>

    <Tooltip title="Bingo">
      <IconButton
        aria-label="Bingo"
        onClick={() => scrollTo(bingoRef)}
        sx={{
          bgcolor: COLORS.red,
          color: "#fff",
          borderRadius: 2,
          "&:hover": { bgcolor: COLORS.redHover },
        }}
      >
        <CasinoIcon />
      </IconButton>
    </Tooltip>
  </Stack>
</Paper>
        </Box>
      )}
    </>
  );
};

export default UserInteractionPage;
